import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, ClipboardList, PlusCircle, BarChart3, Droplet, LogOut, UserCircle, HelpCircle, Bug, X, ChevronDown, Users, Key, Building2, CreditCard, Wrench, Menu, MapPin } from 'lucide-react';
import { cn } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { getAvatarUrl, clearAvatarCache, getCachedAvatarUrl, batchGetAvatarUrls } from '../lib/avatarUtils';
import { usePermissionsRefresh } from '../contexts/PermissionsContext';
import { useBranchStore } from '../store/branchStore';
import { useBranchesPreload } from '../hooks/useBranchesPreload';
import DeveloperMenu from './DeveloperMenu';
import BugReportModal from './BugReportModal';
import BranchSelector from './BranchSelector';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Requests', href: '/requests', icon: ClipboardList },
  { name: 'Create Request', href: '/create-request', icon: PlusCircle },
  { name: 'Reports', href: '/reports', icon: BarChart3 },
  { name: 'Help', href: '/help', icon: HelpCircle, newTab: true },
];

const settingsMenu = [
  { name: 'Users', href: '/settings/users', icon: Users, roles: ['company_owner', 'manager'] },
  { name: 'Roles', href: '/settings/roles', icon: Key, roles: ['company_owner'] },
  { name: 'Company', href: '/settings/company', icon: Building2, roles: ['company_owner', 'manager'] },
  { name: 'Branches', href: '/settings/branches', icon: MapPin, roles: ['company_owner', 'manager'] },
  { name: 'Billing', href: '/settings/billing', icon: CreditCard, roles: ['company_owner'] },
  { name: 'Setup', href: '/settings/setup', icon: Wrench, roles: ['company_owner'] },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Pre-load branches as soon as user is available
  useBranchesPreload(user?.id);

  const [showBugReportModal, setShowBugReportModal] = useState(false);
  const [expandedSettings, setExpandedSettings] = useState(false);
  const [expandedDeveloper, setExpandedDeveloper] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [userPermissions, setUserPermissions] = useState<Record<string, { view?: boolean }>>({});
  const [loadingPermissions, setLoadingPermissions] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Register refresh callback in context so other components can trigger it
  const { setRefreshCallback: setPermissionsRefreshCallback } = usePermissionsRefresh();


  // Close sidebar when window is resized to desktop size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch user permissions from roles endpoint - MUST be defined BEFORE the useEffect that uses it
  const fetchUserPermissions = React.useCallback(async () => {
    try {
      setLoadingPermissions(true);
      const res = await api.get<any>('/roles');
      // Parse permissions from response
      if (res.roles && Array.isArray(res.roles)) {
        const userRole = res.roles.find((r: any) => r.name === user?.role);
        if (userRole && userRole.permissions) {
          const perms = typeof userRole.permissions === 'string'
            ? JSON.parse(userRole.permissions)
            : userRole.permissions;
          setUserPermissions(perms);
          console.log('[Layout] Loaded user permissions for role', user?.role, ':', perms);
        } else {
          console.log('[Layout] No permissions found for role:', user?.role);
          setUserPermissions({});
        }
      }
    } catch (error) {
      console.error('[Layout] Failed to fetch permissions:', error);
      // On error, set empty permissions (hide menu items) but don't crash
      // This can happen on first login after accepting invite code
      setUserPermissions({});
    } finally {
      setLoadingPermissions(false);
    }
  }, [user?.role]);

  // Register refresh callback in context so other components can trigger it
  useEffect(() => {
    setPermissionsRefreshCallback(fetchUserPermissions);
  }, [fetchUserPermissions, setPermissionsRefreshCallback]);

  // Fetch permissions whenever user role changes
  useEffect(() => {
    if (user?.id && user?.role) {
      fetchUserPermissions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, user?.id]);

  // Use avatar directly from user object since it now contains the full Supabase signed URL
  useEffect(() => {
    const hasAvatar = user?.avatar && user.avatar !== 'null' && user.avatar !== null;
    if (hasAvatar) {
      setAvatarUrl(user.avatar);
      setAvatarLoading(false);
    } else {
      setAvatarUrl(null);
      setAvatarLoading(false);
    }
  }, [user?.avatar, user?.id]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const visibleSettingsItems = settingsMenu.filter((item) => {
    // Map menu item names to permission resource names
    const resourceMap: Record<string, string> = {
      'Users': 'users',
      'Roles': 'roles',
      'Company': 'company',
      'Branches': 'branches',
      'Billing': 'billing',
      'Setup': 'setup',
    };

    const resource = resourceMap[item.name];
    // Show item only if user has view permission for that resource
    return resource && userPermissions[resource]?.view === true;
  });

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col lg:flex-row">
      {/* Mobile Header - Only show on small screens */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 z-50">
        <div className="flex items-center gap-2">
          <Droplet className="h-6 w-6 text-cyan-600" />
          <span className="text-xl font-bold text-gray-900">AquaFlow</span>
        </div>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 rounded-lg hover:bg-gray-100"
        >
          {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Sidebar - Hidden on mobile, visible on desktop */}
      <div
        className={cn(
          // Base: hidden on mobile, shown on desktop
          'hidden lg:flex',
          // Always visible structure on desktop
          'bg-white border-r border-gray-200 flex flex-col z-40 w-64'
        )}
      >
        {/* Desktop-only header */}
        <div className="hidden lg:flex items-start gap-3 px-6 py-6 border-b border-gray-200">
          <Droplet className="h-10 w-10 text-cyan-600 flex-shrink-0 pt-0.5" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900 leading-none">AquaFlow</h1>
            <p className="text-sm font-medium text-gray-500 leading-tight">Water Management</p>
          </div>
        </div>

        {/* Branch Selector */}
        <BranchSelector />

        <nav className="flex-1 mt-6 px-3 overflow-y-auto">
          {navigation.map((item) => {
            // Hide Create Request if user doesn't have create permission
            if (item.name === 'Create Request' && !userPermissions.requests?.create) {
              return null;
            }
            if (item.action) {
              return (
                <button
                  key={item.name}
                  onClick={() => setShowBugReportModal(true)}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-red-700 hover:bg-red-50 transition-colors mt-4"
                >
                  <item.icon className="h-5 w-5" />
                  {item.name}
                </button>
              );
            }
            if (item.newTab) {
              return (
                <a
                  key={item.name}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors mb-1"
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="h-5 w-5" />
                  {item.name}
                </a>
              );
            }
            return (
              <NavLink
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors mb-1',
                    isActive
                      ? 'bg-cyan-50 text-cyan-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  )
                }
              >
                <item.icon className="h-5 w-5" />
                {item.name}
              </NavLink>
            );
          })}

          {/* Settings Menu */}
          {visibleSettingsItems.length > 0 && (
            <div className="mt-6 border-t border-gray-200 pt-4">
              <button
                onClick={() => setExpandedSettings(!expandedSettings)}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <span className="flex items-center gap-3">
                  <Wrench className="h-5 w-5" />
                  Settings
                </span>
                <ChevronDown
                  className={cn(
                    'h-4 w-4 transition-transform',
                    expandedSettings && 'rotate-180'
                  )}
                />
              </button>

              {expandedSettings && (
                <div className="mt-2 space-y-1">
                  {visibleSettingsItems.map((item) => (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ml-4 mb-1',
                          isActive
                            ? 'bg-cyan-50 text-cyan-700'
                            : 'text-gray-600 hover:bg-gray-100'
                        )
                      }
                    >
                      <item.icon className="h-4 w-4" />
                      {item.name}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Developer Menu - Below Settings */}
          <DeveloperMenu expanded={expandedDeveloper} onToggle={() => setExpandedDeveloper(!expandedDeveloper)} />

          {/* Report Bug Button - Below Developer */}
          <button
            onClick={() => setShowBugReportModal(true)}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-red-700 hover:bg-red-50 transition-colors mt-4"
          >
            <Bug className="h-5 w-5" />
            Report Bug
          </button>
        </nav>

        {/* User Info and Logout */}
        <div className="border-t border-gray-200 p-4">
          <div className="flex items-center gap-3 mb-3 px-2">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center text-white flex-shrink-0 overflow-hidden relative">
              {avatarLoading ? (
                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : avatarUrl ? (
                <img src={avatarUrl} alt={user?.name || 'User'} className="w-full h-full object-cover" />
              ) : (
                <UserCircle className="h-5 w-5" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.name || user?.email?.split('@')[0] || 'User'}
              </p>
              <p className="text-sm font-medium text-gray-600 capitalize mt-1">
                {user?.role.replace('_', ' ') || 'User'}
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 text-red-700 text-sm font-medium hover:bg-red-50 rounded-lg transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </button>
        </div>
      </div>

      {/* Mobile Sidebar - Slides in from left on mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-y-0 left-0 w-64 bg-white border-r border-gray-200 flex flex-col z-40 pt-16 lg:hidden"
        >
          {/* Mobile header inside sidebar */}
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-900">Menu</h2>
          </div>

          {/* Branch Selector */}
          <BranchSelector />

          {/* Scrollable content area */}
          <div className="flex-1 flex flex-col overflow-y-auto">
            <nav className="px-3 pt-6">
              {navigation.map((item) => {
              // Hide Create Request if user doesn't have create permission
              if (item.name === 'Create Request' && !userPermissions.requests?.create) {
                return null;
              }
              if (item.action) {
                return (
                  <button
                    key={item.name}
                    onClick={() => {
                      setShowBugReportModal(true);
                      setSidebarOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-red-700 hover:bg-red-50 transition-colors mt-4"
                  >
                    <item.icon className="h-5 w-5" />
                    {item.name}
                  </button>
                );
              }
              if (item.newTab) {
                return (
                  <a
                    key={item.name}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors mb-1"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <item.icon className="h-5 w-5" />
                    {item.name}
                  </a>
                );
              }
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors mb-1',
                      isActive
                        ? 'bg-cyan-50 text-cyan-700'
                        : 'text-gray-700 hover:bg-gray-100'
                    )
                  }
                >
                  <item.icon className="h-5 w-5" />
                  {item.name}
                </NavLink>
              );
            })}
            </nav>

            {/* Settings Menu - Mobile */}
            {visibleSettingsItems.length > 0 && (
              <div className="mt-6 border-t border-gray-200 pt-4 px-3">
                <button
                  onClick={() => setExpandedSettings(!expandedSettings)}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <span className="flex items-center gap-3">
                    <Wrench className="h-5 w-5" />
                    Settings
                  </span>
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 transition-transform',
                      expandedSettings && 'rotate-180'
                    )}
                  />
                </button>

                {expandedSettings && (
                  <div className="mt-2 space-y-1">
                    {visibleSettingsItems.map((item) => (
                      <NavLink
                        key={item.name}
                        to={item.href}
                        onClick={() => {
                          setSidebarOpen(false);
                          setExpandedSettings(false);
                        }}
                        className={({ isActive }) =>
                          cn(
                            'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ml-4 mb-1',
                            isActive
                              ? 'bg-cyan-50 text-cyan-700'
                              : 'text-gray-600 hover:bg-gray-100'
                          )
                        }
                      >
                        <item.icon className="h-4 w-4" />
                        {item.name}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Developer Menu - Mobile */}
            <div className="mt-4 border-t border-gray-200 pt-4 px-3">
              <button
                onClick={() => setExpandedDeveloper(!expandedDeveloper)}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <span className="flex items-center gap-3">
                  <Wrench className="h-5 w-5" />
                  Developer
                </span>
                <ChevronDown
                  className={cn(
                    'h-4 w-4 transition-transform',
                    expandedDeveloper && 'rotate-180'
                  )}
                />
              </button>

              {expandedDeveloper && (
                <div className="mt-2 space-y-1">
                  <NavLink
                    to="/developer/overview"
                    onClick={() => {
                      setSidebarOpen(false);
                      setExpandedDeveloper(false);
                    }}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ml-4 mb-1',
                        isActive
                          ? 'bg-cyan-50 text-cyan-700'
                          : 'text-gray-600 hover:bg-gray-100'
                      )
                    }
                  >
                    Overview
                  </NavLink>
                  <NavLink
                    to="/developer/api"
                    onClick={() => {
                      setSidebarOpen(false);
                      setExpandedDeveloper(false);
                    }}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ml-4 mb-1',
                        isActive
                          ? 'bg-cyan-50 text-cyan-700'
                          : 'text-gray-600 hover:bg-gray-100'
                      )
                    }
                  >
                    API
                  </NavLink>
                  <NavLink
                    to="/developer/database"
                    onClick={() => {
                      setSidebarOpen(false);
                      setExpandedDeveloper(false);
                    }}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ml-4 mb-1',
                        isActive
                          ? 'bg-cyan-50 text-cyan-700'
                          : 'text-gray-600 hover:bg-gray-100'
                      )
                    }
                  >
                    Database
                  </NavLink>
                  <NavLink
                    to="/developer/auth"
                    onClick={() => {
                      setSidebarOpen(false);
                      setExpandedDeveloper(false);
                    }}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ml-4 mb-1',
                        isActive
                          ? 'bg-cyan-50 text-cyan-700'
                          : 'text-gray-600 hover:bg-gray-100'
                      )
                    }
                  >
                    Auth
                  </NavLink>
                  <NavLink
                    to="/developer/roles"
                    onClick={() => {
                      setSidebarOpen(false);
                      setExpandedDeveloper(false);
                    }}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ml-4 mb-1',
                        isActive
                          ? 'bg-cyan-50 text-cyan-700'
                          : 'text-gray-600 hover:bg-gray-100'
                      )
                    }
                  >
                    Roles
                  </NavLink>
                  <NavLink
                    to="/developer/logic"
                    onClick={() => {
                      setSidebarOpen(false);
                      setExpandedDeveloper(false);
                    }}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ml-4 mb-1',
                        isActive
                          ? 'bg-cyan-50 text-cyan-700'
                          : 'text-gray-600 hover:bg-gray-100'
                      )
                    }
                  >
                    Logic
                  </NavLink>
                  <NavLink
                    to="/developer/user-management"
                    onClick={() => {
                      setSidebarOpen(false);
                      setExpandedDeveloper(false);
                    }}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ml-4 mb-1',
                        isActive
                          ? 'bg-cyan-50 text-cyan-700'
                          : 'text-gray-600 hover:bg-gray-100'
                      )
                    }
                  >
                    User Management
                  </NavLink>
                  <NavLink
                    to="/developer/architecture"
                    onClick={() => {
                      setSidebarOpen(false);
                      setExpandedDeveloper(false);
                    }}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ml-4 mb-1',
                        isActive
                          ? 'bg-cyan-50 text-cyan-700'
                          : 'text-gray-600 hover:bg-gray-100'
                      )
                    }
                  >
                    Architecture
                  </NavLink>
                  <NavLink
                    to="/developer/demo"
                    onClick={() => {
                      setSidebarOpen(false);
                      setExpandedDeveloper(false);
                    }}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ml-4 mb-1',
                        isActive
                          ? 'bg-cyan-50 text-cyan-700'
                          : 'text-gray-600 hover:bg-gray-100'
                      )
                    }
                  >
                    Demo
                  </NavLink>
                </div>
              )}
            </div>
          </div>

          {/* User Info */}
          <div className="border-t border-gray-200 p-4">
            <div className="flex items-center gap-3 mb-3 px-2">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center text-white flex-shrink-0 overflow-hidden relative">
                {avatarLoading ? (
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : avatarUrl ? (
                  <img src={avatarUrl} alt={user?.name || 'User'} className="w-full h-full object-cover" />
                ) : (
                  <UserCircle className="h-5 w-5" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.name || user?.email?.split('@')[0] || 'User'}
                </p>
                <p className="text-sm font-medium text-gray-600 capitalize mt-1">
                  {user?.role.replace('_', ' ') || 'User'}
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                handleLogout();
                setSidebarOpen(false);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-red-700 text-sm font-medium hover:bg-red-50 rounded-lg transition-colors"
            >
              <LogOut className="h-4 w-4" />
              Sign Out
            </button>
          </div>
        </div>
      )}

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 pt-16 lg:pt-0 w-full lg:w-auto">
        <Outlet />
      </div>

      {/* Bug Report Modal */}
      <BugReportModal isOpen={showBugReportModal} onClose={() => setShowBugReportModal(false)} />
    </div>
  );
}
